import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import AuthImage from "@/components/AuthImage";

export default function PhotoLightbox({ photos, index, onClose, onIndex }) {
  const touchX = useRef(null);
  const [dir, setDir] = useState(0);
  const has = index >= 0 && index < photos.length;

  const go = (delta) => {
    const next = index + delta;
    if (next < 0 || next >= photos.length) return;
    setDir(delta);
    onIndex(next);
  };

  useEffect(() => {
    if (!has) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  });

  if (!has) return null;
  const p = photos[index];

  const onTouchStart = (e) => (touchX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/90 backdrop-blur-sm flex flex-col"
      data-testid="photo-lightbox"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center justify-between px-4 sm:px-6 h-16 text-white/90" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm">
          <span className="font-medium">{p.filename || "Foto"}</span>
          <span className="text-white/50 ml-3">{index + 1} / {photos.length}</span>
        </div>
        <button onClick={onClose} data-testid="lightbox-close" className="p-2 rounded-full hover:bg-white/10 transition-colors">
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-16 pb-8 min-h-0" onClick={(e) => e.stopPropagation()}>
        <AuthImage
          key={p.id + dir}
          path={`/order-photos/${p.id}/download`}
          alt={p.filename}
          className="max-h-full max-w-full object-contain rounded-lg animate-in fade-in duration-200"
        />
      </div>

      {index > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); go(-1); }}
          data-testid="lightbox-prev"
          className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}
      {index < photos.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); go(1); }}
          data-testid="lightbox-next"
          className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
